"use client";

import { useState, useEffect } from "react";
import Link from "next/link";
import Image from "next/image";
import { useGetPartList, useGetCategoryList } from "@/lib/api/hooks";
import { useI18n, getLocalizedField } from "@/i18n";

const getConditionName = (condition, t) => {
  const names = {
    new: t("parts.condition.new"),
    used: t("parts.condition.used"),
    refurbished: t("parts.condition.refurbished"),
  };
  return names[condition] ?? "";
};

const getPartTypeName = (partType, t) => {
  const names = {
    original: t("parts.partType.original"),
    aftermarket: t("parts.partType.aftermarket"),
    salvage: t("parts.partType.salvage"),
  };
  return names[partType] ?? "";
};

const formatPrice = (price) => {
  if (price == null || isNaN(price)) return "-";
  return `GNF ${(price / 1000000).toFixed(0)}M`;
};

export default function PartsListPage() {
  const { t, lang } = useI18n();

  const SORT_OPTIONS = [
    { value: "newest", label: t("parts.sortNewest") },
    { value: "priceAsc", label: t("parts.sortPriceAsc") },
    { value: "priceDesc", label: t("parts.sortPriceDesc") },
  ];

  const [pageNum, setPageNum] = useState(1);
  const [pageSize] = useState(20);
  const [categoryId, setCategoryId] = useState(null);
  const [keyword, setKeyword] = useState("");
  const [sort, setSort] = useState("newest");

  // 从 URL 参数读取 categoryId，支持从热门配件分类跳转
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const id = params.get("categoryId");
    if (id) {
      setCategoryId(Number(id));
    }
  }, []);

  const params = { pageNum, pageSize, sort };
  if (categoryId) params.categoryId = categoryId;
  if (keyword.trim()) params.keyword = keyword.trim();

  const { data, isLoading } = useGetPartList(params);
  const { data: categoryData } = useGetCategoryList('part');
  const categories = categoryData?.data ?? categoryData ?? [];
  const records = data?.data ?? [];
  const totalPages = data?.pagination?.total ? Math.ceil(data.pagination.total / pageSize) : 1;

  const handleCategoryClick = (id) => {
    setCategoryId(id);
    setPageNum(1);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPageNum(1);
  };

  return (<div className="dealers-page">
    <div className="page-header" style={{
      background: "linear-gradient(135deg, rgba(0,0,0,0.65) 0%, rgba(0,0,0,0.35) 50%, rgba(0,0,0,0.55) 100%), url('https://images.unsplash.com/photo-1486262715619-67b85e0b08d3?w=1600&q=80') center/cover no-repeat",
      borderBottom: "1px solid rgba(255,255,255,0.1)",
      padding: "40px 0 32px",
      position: "relative",
    }}>
      <div className="container">
        <div className="breadcrumb">
          <Link href="/">{t("common.home")}</Link>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="m9 18 6-6-6-6"/></svg>
          <Link href="/parts" style={{ color: "rgba(255,255,255,0.7)" }}>{t("nav.parts")}</Link>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="m9 18 6-6-6-6"/></svg>
          <span>{t("parts.allParts")}</span>
        </div>
        <h1 className="page-title" style={{ textShadow: "0 2px 8px rgba(0,0,0,0.5)" }}>{t("parts.allParts")}</h1>
        <p className="page-subtitle" style={{ textShadow: "0 1px 4px rgba(0,0,0,0.4)" }}>{t("parts.allPartsSubtitle")}</p>
      </div>
    </div>

    <div className="container" style={{ paddingTop: 0, paddingBottom: "var(--space-8)" }}>
      <div className="filter-bar mb-6" style={{ borderRadius: "var(--radius-md)", border: "1px solid var(--border)", padding: "16px 20px", marginTop: 16, alignItems: "flex-start" }}>
        <div style={{ display: "flex", alignItems: "flex-start", gap: 10, flexWrap: "wrap", flex: 1 }}>
          <button
            className={`filter-chip ${categoryId === null ? "active" : ""}`}
            onClick={() => handleCategoryClick(null)}
          >{t("parts.allCategories")}</button>
          {categories.map(c => (
            <button
              key={c.id}
              className={`filter-chip ${categoryId === c.id ? "active" : ""}`}
              onClick={() => handleCategoryClick(c.id)}
            >{getLocalizedField(c, "categoryName", lang)}</button>
          ))}
        </div>
        <div className="filter-sort" style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "var(--color-text-secondary)" }}>
          <form onSubmit={handleSearch} style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <input
              type="text"
              placeholder={t("parts.searchPlaceholder")}
              value={keyword}
              onChange={e => setKeyword(e.target.value)}
              style={{
                height: 34, padding: "0 10px", border: "1px solid var(--color-border)",
                borderRadius: 8, fontSize: 13, outline: "none", width: 140,
                background: "var(--color-surface)"
              }}
            />
            <button type="submit" className="btn btn-primary btn-sm" style={{ height: 30, padding: "0 12px", fontSize: 12 }}>{t("common.search")}</button>
          </form>
          <span>{t("parts.sortBy")}</span>
          <select
            value={sort}
            onChange={e => { setSort(e.target.value); setPageNum(1); }}
            style={{ border: "none", background: "transparent", fontWeight: 600, color: "var(--color-text)", cursor: "pointer", fontSize: 13 }}
          >
            {SORT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
      </div>

      <div className="grid grid-cols-4" style={{ marginTop: "44px" }}>
        {isLoading ? Array.from({ length: 8 }).map((_, i) => (
          <div key={`skeleton-${i}`} className="part-card">
            <div className="part-card-image" style={{ background: "var(--color-bg-alt)" }}>
              <div style={{ width: "100%", height: "100%", background: "linear-gradient(90deg, var(--color-bg-alt) 25%, rgba(255,255,255,0.1) 50%, var(--color-bg-alt) 75%)", backgroundSize: "200% 100%", animation: "shimmer 1.5s ease-in-out infinite" }}/>
            </div>
            <div style={{ padding: "0 20px 20px 20px" }}>
              <div style={{ height: 14, width: "60%", background: "var(--color-bg-alt)", borderRadius: 4, marginBottom: 8, animation: "shimmer 1.5s ease-in-out infinite" }}/>
              <div style={{ height: 14, width: "80%", background: "var(--color-bg-alt)", borderRadius: 4, marginBottom: 8 }}/>
              <div style={{ height: 14, width: "40%", background: "var(--color-bg-alt)", borderRadius: 4, marginBottom: 8, animation: "shimmer 1.5s ease-in-out infinite" }}/>
              <div style={{ height: 20, width: "50%", background: "var(--color-bg-alt)", borderRadius: 4 }}/>
            </div>
          </div>
        )) : records.length === 0 ? (
          <div style={{ gridColumn: "1/-1", padding: "60px 0", textAlign: "center", color: "var(--text-muted)" }}>
            <Image src="https://iyoubucket.oss-cn-hangzhou.aliyuncs.com/guineemall/part/part2.png" alt="No Data" width={60} height={60} style={{ margin: "0 auto 10px" }}/>
            <div style={{ fontSize: 14, color: "#8a8a8a" }}>{t("parts.noParts")}</div>
          </div>
        ) : records.map((p) => (<Link key={p.id} href={`/parts/${p.id}`} className="part-card" style={{ textDecoration: "none", color: "inherit", display: "block" }}>
          <div className="part-card-image">
            <Image src={p.coverUrl} alt={p.title} layout="fill" objectFit="cover"/>
            <div className="car-card-badges">
              {p.isFeatured ? <span className="tag tag-success">{t("parts.promoted")}</span> : null}
            </div>
          </div>
          <div style={{ padding: "0 20px 20px 20px" }}>
            <div className="flex items-center justify-between gap-2 mb-2">
              <span className={`tag tag-${p.condition === 'new' ? 'brand' : 'success'}`}>{getConditionName(p.condition, t)}</span>
              <span className="text-sm text-muted" style={{ color: "var(--color-text-muted)" }}>{getLocalizedField(p, "modelName", lang)} {getLocalizedField(p, "brandName", lang)} {getPartTypeName(p.partType, t)}</span>
            </div>
            <div className="font-bold mb-1">{p.title}</div>
            <div className="text-sm text-muted mb-3" style={{ color: "var(--color-text-muted)" }}>{getLocalizedField(p, "cityName", lang)}</div>
            <div className={`car-card-price ${p.priceNegotiable === 1 ? "negotiable" : ""}`}>{p.priceNegotiable === 1 ? "NEGOTIABLE" : formatPrice(p.priceGnf)}</div>
          </div>
        </Link>))}
      </div>

      <div className="pagination mb-12" style={{ marginTop: 52 }}>
	        <button
	          disabled={pageNum <= 1}
	          onClick={() => setPageNum(1)}
	        >«</button>
	        <button
	          disabled={pageNum <= 1}
	          onClick={() => setPageNum(prev => prev - 1)}
	        >‹</button>
	        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
	          let pageNumBtn;
	          if (totalPages <= 5) {
	            pageNumBtn = i + 1;
	          } else if (pageNum <= 3) {
	            pageNumBtn = i + 1;
	          } else if (pageNum >= totalPages - 2) {
	            pageNumBtn = totalPages - 4 + i;
	          } else {
	            pageNumBtn = pageNum - 2 + i;
	          }
	          return (
	            <button
	              key={pageNumBtn}
	              className={pageNum === pageNumBtn ? "active" : ""}
	              onClick={() => setPageNum(pageNumBtn)}
	            >{pageNumBtn}</button>
	          );
	        })}
	        <button
	          disabled={pageNum >= totalPages}
	          onClick={() => setPageNum(prev => prev + 1)}
	        >›</button>
	        <button
	          disabled={pageNum >= totalPages}
	          onClick={() => setPageNum(totalPages)}
	        >»</button>
	      </div>
    </div>
  </div>);
}